import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { useTranslate } from '../lib/i18n/useTranslate';
import Button from './common/Button';
import { TagPill } from './TagPill';

export type QuestionData = {
  question: string;
  options: {
    heading: string;
    translationKey?: string;
    subtext?: string;
  }[];
  helpText?: string;
};

interface CenteredQuestionProps {
  question: QuestionData;
  onOptionSelect: (optionIndex: number) => void;
}

interface OptionConfirmationProps {
  option: QuestionData['options'][0];
  onOptionSelect: () => void;
}

const OptionConfirmation = ({ option, onOptionSelect }: OptionConfirmationProps) => {
  const t = useTranslate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  return (
    <div className="mb-4">
      {!showDisclaimer && (
        <Button variant="secondary" onClick={() => setShowDisclaimer(true)}>
          <div className="text-foreground">{option.heading}</div>
          {option.subtext && <div className="text-sm text-foreground">{option.subtext}</div>}
        </Button>
      )}
      {showDisclaimer && (
        <div className="border rounded-md text-sm p-3 bg-muted">
          <div className="flex justify-end">
            <TagPill text={t('survey.victimIsMinor')} />
          </div>
          <div className="flex px-2 mt-4">
            <Check className="size-5 mr-3" />
            <p className="flex-1">{t('riku.ageMinorDisclaimer.line1')}</p>
          </div>
          <div className="flex px-2 mt-4">
            <Check className="size-5 mr-3" />
            <p className="flex-1">{t('riku.ageMinorDisclaimer.line2')}</p>
          </div>
          <div className="flex px-2 mt-4">
            <Check className="size-5 mr-3" />
            <p className="flex-1">{t('riku.ageMinorDisclaimer.line3')}</p>
          </div>
          <div className="flex px-2 mt-4">
            <Check className="size-5 mr-3" />
            <p className="flex-1">{t('riku.ageMinorDisclaimer.line4')}</p>
          </div>
          <div className="flex px-2 mt-4">
            <Check className="size-5 mr-3" />
            <p className="flex-1">{t('riku.ageMinorDisclaimer.line5')}</p>
          </div>
          <Button className="mt-4 w-full" variant="primary" onClick={() => onOptionSelect()}>
            {t('riku.ageMinorDisclaimer.accept')}
          </Button>
        </div>
      )}
    </div>
  );
};

const CenteredQuestion = ({ question, onOptionSelect }: CenteredQuestionProps) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [question]);

  return (
    <div className="min-h-full flex flex-col max-w-lg mx-auto justify-between items-center">
      <div className="w-full mt-auto mb-auto space-y-4">
        <div className="text-center">{question.question}</div>
        {question.options.map((option, optionIndex) => {
          // Logic specific to Riku's age minor option
          if (option.translationKey === 'riku.ageMinor') {
            return (
              <OptionConfirmation
                key={optionIndex}
                option={option}
                onOptionSelect={() => onOptionSelect(optionIndex)}
              />
            );
          }

          return (
            <Button
              key={optionIndex}
              variant="secondary"
              onClick={() => onOptionSelect(optionIndex)}
            >
              <div className="text-foreground">{option.heading}</div>
              {option.subtext && <div className="text-sm text-foreground">{option.subtext}</div>}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default CenteredQuestion;
